import {
  ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Optional, Output,
} from '@angular/core';
import { AccessService } from '../../../services/AccessService';
import {
  NavigationEnd, Router,
} from '@angular/router';
import { Subscription } from 'rxjs';
import { EventApiService } from '../../../../events/services/event-api/EventApiService';

type SidebarSection = 'crm' | 'apps' | 'finance' | 'directories' | 'system' | 'integrations';

@Component({
  selector: 'app-sidebar',
  standalone: false,
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.sass',
})
export class SidebarComponent implements OnInit, OnDestroy {
  @Input()
  public collapsed = false;

  @Output()
  public readonly toggleCollapsed = new EventEmitter<void>();

  @Output()
  public readonly navigated = new EventEmitter<void>();

  public sectionOpen: Record<SidebarSection, boolean> = {
    crm: true,
    apps: false,
    finance: false,
    directories: false,
    system: false,
    integrations: false,
  };
  public activeEventsCount = 0;
  private routerEventsSubscription: Subscription | null = null;
  private readonly sectionPaths: Record<SidebarSection, string[]> = {
    crm: [
      '/dashboard/events',
      '/dashboard/tickets',
      '/dashboard/clients',
      '/dashboard/mailings',
    ],
    apps: [
      '/dashboard/kanban',
      '/dashboard/calendar',
    ],
    finance: [
      '/dashboard/settlements',
      '/dashboard/orders',
      '/dashboard/expenses',
    ],
    directories: [
      '/dashboard/championship',
      '/dashboard/teams',
      '/dashboard/season',
    ],
    system: [
      '/dashboard/users',
      '/dashboard/notifications',
      '/dashboard/settings',
    ],
    integrations: [
      '/dashboard/telegram',
      '/dashboard/minio',
      '/dashboard/bank',
    ],
  };

  public constructor(
    private accessService: AccessService,
    private router: Router,
    private changeDetector: ChangeDetectorRef,
    @Optional() private eventApi: EventApiService | null,
  ) { }

  public ngOnInit(): void {
    this.openActiveSections();
    void this.loadActiveEventsCount();
    this.routerEventsSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.openActiveSections();
        void this.loadActiveEventsCount();
      }
    });
  }

  public ngOnDestroy(): void {
    this.routerEventsSubscription?.unsubscribe();
  }

  public isSectionActive(section: SidebarSection): boolean {
    return this.sectionPaths[section].some((path) => this.router.url === path || this.router.url.startsWith(`${path}/`));
  }

  public toggleSection(section: SidebarSection): void {
    if (this.collapsed) {
      this.toggleCollapsed.emit();
      this.sectionOpen[section] = true;
      return;
    }

    this.sectionOpen[section] = !this.sectionOpen[section];
  }

  public notifyNavigation(): void {
    this.navigated.emit();
  }

  public async logout(): Promise<void> {
    this.accessService.unauthorize();
    await this.router.navigate([
      'sign-in',
    ]);
  }

  private async loadActiveEventsCount(): Promise<void> {
    if (!this.eventApi) {
      return;
    }

    try {
      const response = await this.eventApi.getEvents();
      if (response.status === 'successful') {
        this.activeEventsCount = response.response.filter((event) => event.status === 'in_progress').length;
      }
    } catch {
      this.activeEventsCount = 0;
    } finally {
      this.changeDetector.detectChanges();
    }
  }

  private openActiveSections(): void {
    (Object.keys(this.sectionPaths) as SidebarSection[]).forEach((section) => {
      if (this.isSectionActive(section)) {
        this.sectionOpen[section] = true;
      }
    });
  }
}
